
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { COUNTRIES } from '@/utils/countries';

export interface EnhancedBookData {
  bookId: string;
  unitsSold: number;
  grossSales: number;
  netSales: number; // This represents Previous Royalty Income
  royaltyAmount: number;
  bookFormat: 'hardcover' | 'paperback' | 'ebook' | 'audiobook';
  country: string;
  formatId: string;
  reportId?: string;
}

interface EnhancedBookSelectionCardProps {
  title: string;
  books: any[];
  hasSelectedFormats: boolean;
  bookFormatGroups: Record<string, EnhancedBookData[]>;
  onUpdateBookData: (formatId: string, field: keyof Omit<EnhancedBookData, 'bookId' | 'formatId'>, value: number | string) => void;
  onAddFormat: (bookId: string) => void;
  onRemoveFormat: (formatId: string) => void;
  form: UseFormReturn<any>;
}

const BOOK_FORMATS = [
  { value: 'hardcover', label: 'Hardcover' },
  { value: 'paperback', label: 'Paperback' },
  { value: 'ebook', label: 'E-book' },
  { value: 'audiobook', label: 'Audiobook' },
];

const EnhancedBookSelectionCard: React.FC<EnhancedBookSelectionCardProps> = ({
  title,
  books,
  hasSelectedFormats,
  bookFormatGroups,
  onUpdateBookData,
  onAddFormat,
  onRemoveFormat,
  form
}) => {
  // Get all formats for all books with this title
  const allFormats = React.useMemo(() => {
    const formats: EnhancedBookData[] = [];
    books.forEach(book => {
      const bookFormats = bookFormatGroups[book.id] || [];
      formats.push(...bookFormats);
    });
    return formats;
  }, [books, bookFormatGroups]);

  const handleAddFormat = () => {
    // Add format to the first book by default
    if (books.length > 0) {
      onAddFormat(books[0].id);
    }
  };

  const handleBookIdChange = (formatId: string, newBookId: string) => {
    // Update the bookId for this format
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    const updatedBooks = currentBooks.map(book => 
      book.formatId === formatId ? { ...book, bookId: newBookId } : book
    );
    form.setValue('books', updatedBooks as EnhancedBookData[]);
  };

  // Handle field changes with proper calculation
  const handleFieldChange = (formatId: string, field: 'grossSales' | 'netSales' | 'unitsSold', value: number) => {
    const currentBooks = form.getValues('books') as EnhancedBookData[];
    const bookIndex = currentBooks.findIndex(book => book.formatId === formatId);
    
    if (bookIndex !== -1) {
      const updatedBooks = [...currentBooks];
      updatedBooks[bookIndex] = { ...updatedBooks[bookIndex], [field]: value };
      
      // Calculate royalty amount: Gross Sales + Previous Royalty Income = Royalty Amount
      if (field === 'grossSales' || field === 'netSales') {
        const grossSales = field === 'grossSales' ? value : updatedBooks[bookIndex].grossSales;
        const previousRoyaltyIncome = field === 'netSales' ? value : updatedBooks[bookIndex].netSales;
        updatedBooks[bookIndex].royaltyAmount = grossSales + previousRoyaltyIncome;
      }
      
      form.setValue('books', updatedBooks as EnhancedBookData[]);
    }
  };

  return (
    <Card className="border border-gray-200">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <div className="text-sm text-muted-foreground mt-1 space-y-1">
              {books.map((book, index) => (
                <div key={book.id} className="flex items-center gap-2">
                  <span>ISBN: {book.isbn}</span>
                  <Badge variant="secondary" className="text-xs">
                    {(book.format || 'paperback').charAt(0).toUpperCase() + (book.format || 'paperback').slice(1)}
                  </Badge>
                  {books.length > 1 && <span className="text-xs">({index + 1} of {books.length})</span>}
                </div>
              ))}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddFormat}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Format
          </Button>
        </div>
      </CardHeader>
      
      {allFormats.length > 0 && (
        <CardContent className="space-y-4">
          {allFormats.map((format) => {
            const book = books.find(b => b.id === format.bookId);
            return (
              <div key={format.formatId} className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="capitalize">
                      {format.bookFormat}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        ISBN: {book?.isbn}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {(book?.format || 'paperback').charAt(0).toUpperCase() + (book?.format || 'paperback').slice(1)} Format
                      </Badge>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveFormat(format.formatId)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor={`format-${format.formatId}`}>Format</Label>
                    <Select
                      value={format.bookFormat}
                      onValueChange={(value) => onUpdateBookData(format.formatId, 'bookFormat', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BOOK_FORMATS.map((fmt) => (
                          <SelectItem key={fmt.value} value={fmt.value}>
                            {fmt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor={`country-${format.formatId}`}>Country</Label>
                    <Select
                      value={format.country}
                      onValueChange={(value) => onUpdateBookData(format.formatId, 'country', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {books.length > 1 && (
                    <div>
                      <Label htmlFor={`isbn-${format.formatId}`}>Select ISBN</Label>
                      <Select
                        value={format.bookId}
                        onValueChange={(value) => handleBookIdChange(format.formatId, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {books.map((book) => (
                            <SelectItem key={book.id} value={book.id}>
                              <div className="flex items-center gap-2">
                                <span>{book.isbn}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {(book.format || 'paperback').charAt(0).toUpperCase() + (book.format || 'paperback').slice(1)}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`units-${format.formatId}`}>Units Sold</Label>
                    <Input
                      id={`units-${format.formatId}`}
                      type="number"
                      min="0"
                      value={format.unitsSold}
                      onChange={(e) => handleFieldChange(format.formatId, 'unitsSold', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`gross-${format.formatId}`}>Gross Sales ($)</Label>
                    <Input
                      id={`gross-${format.formatId}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={format.grossSales}
                      onChange={(e) => handleFieldChange(format.formatId, 'grossSales', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor={`previous-royalty-${format.formatId}`}>Previous Royalty Income ($)</Label>
                    <Input
                      id={`previous-royalty-${format.formatId}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={format.netSales}
                      onChange={(e) => handleFieldChange(format.formatId, 'netSales', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter 0 if there's no previous royalty income
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor={`royalty-${format.formatId}`}>Royalty Amount ($)</Label>
                    <Input
                      id={`royalty-${format.formatId}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={format.royaltyAmount}
                      readOnly
                      className="bg-gray-50"
                      title="Automatically calculated: Gross Sales + Previous Royalty Income"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Auto-calculated: Gross Sales + Previous Royalty Income
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
};

export default EnhancedBookSelectionCard;
